import React from 'react';
import { Typography } from 'antd';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';

const { Text } = Typography;

interface VisualSelectFieldProps {
  field: any;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  commonStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  error?: string;
}

export const VisualSelectField: React.FC<VisualSelectFieldProps> = ({
  field,
  value,
  onChange,
  commonStyle,
  labelStyle,
  error
}) => {
  const { fontSize, spacing } = useResponsiveSizes();
  const fieldOptions = field.options || [];

  return (
    <div style={commonStyle}>
      {field.label && (
        <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
          {field.label}
        </Text>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`,
          gap: spacing.md,
          width: '100%'
        }}
      >
        {fieldOptions.map((option: any) => {
          const isSelected = value === option.value;

          return (
            <OptionCard
              key={option.value}
              option={option}
              isSelected={isSelected}
              onClick={() => {
                // Si está seleccionada, des-seleccionar (undefined)
                // Si no está seleccionada, seleccionar
                if (isSelected) {
                  onChange(undefined);
                } else {
                  onChange(option.value);
                }
              }}
            />
          );
        })}
      </div>

      {error && (
        <Text type='danger' style={{ fontSize: fontSize.base, marginTop: 4 }}>
          {error}
        </Text>
      )}
    </div>
  );
};

interface OptionCardProps {
  option: any;
  isSelected: boolean;
  onClick: () => void;
}

const OptionCard: React.FC<OptionCardProps> = ({ option, isSelected, onClick }) => {
  const { fontSize, spacing } = useResponsiveSizes();
  const [isHovered, setIsHovered] = React.useState(false);

  const defaultBackground = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
  const hoverBackground = option.backgroundColor || '#0139E3';

  const cardStyle: React.CSSProperties = {
    position: 'relative',
    border: isSelected ? '2px solid #3b82f6' : '2px solid #e2e8f0',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    boxShadow: isSelected
      ? '0 0 0 4px rgba(59, 130, 246, 0.15)'
      : isHovered
        ? '0 12px 24px rgba(0, 0, 0, 0.1)'
        : 'none',
    transform: isHovered && !isSelected ? 'translateY(-4px)' : 'none'
  };

  const previewStyle: React.CSSProperties = {
    height: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    position: 'relative',
    background: isHovered ? hoverBackground : defaultBackground,
    transition: 'background 0.3s ease'
  };

  const imageStyle: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    position: 'relative',
    zIndex: 1,
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
  };

  const labelStyle: React.CSSProperties = {
    padding: spacing.sm,
    textAlign: 'center',
    fontSize: fontSize.lg,
    fontWeight: isSelected ? 600 : 500,
    color: isSelected ? '#3b82f6' : '#475569',
    background: isSelected ? '#eff6ff' : '#fafafa',
    borderTop: '1px solid #e2e8f0',
    transition: 'all 0.25s ease'
  };

  return (
    <div
      style={cardStyle}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={previewStyle}>
        <img src={option.value} alt={option.label} style={imageStyle} />
      </div>

      <div style={labelStyle}>{option.label}</div>
    </div>
  );
};
